import { Copy, ImportCurve } from 'iconsax-react';
import { isEmpty, last } from 'lodash-es';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTypedParams } from 'react-router-typesafe-routes';
import DateDisplay from '@/components/common/date-display';
import MediaContent from '@/components/common/media-content/media-content';
import { NativeShareButton } from '@/components/common/native-share-button';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import { useSafeMediaUrl } from '@/hooks/use-safe-media-url';
import { ConversationMessageContentType, type ConversationMessageTypeUnion } from '@/models/conversations/conversation';
import ROUTES from '@/routes';
import { useMessageStore } from '@/stores/message';
import copyTextToClipboard from '@/utils/copy-text';
import cn from '@/utils/merge-class-names';

export default function MessageDisplayRoute() {
  const { t } = useTranslation();
  const { messageId } = useTypedParams(ROUTES.MESSAGES.GROUPS.MESSAGE_BY_ID || ROUTES.MESSAGES.SESSIONS.MESSAGE_BY_ID);

  const message = useMemo<ConversationMessageTypeUnion>(() => {
    const m = useMessageStore.getState().messages.get(messageId);

    if (!m) return {};

    if (isEmpty(m.Quote)) {
      return m;
    }

    try {
      return JSON.parse(m.Quote || '{}');
    } catch (e) {
      return {};
    }
  }, [messageId]);

  const { src: safeSrc } = useSafeMediaUrl(
    message.MsgType === ConversationMessageContentType.Text ? undefined : message.Message,
  );

  const shouldDarkMode = [ConversationMessageContentType.Image, ConversationMessageContentType.Video].includes(
    message.MsgType,
  );

  const handleDownload = () => {
    if (!message) return;
    const a = document.createElement('a');
    a.download = last(safeSrc.split('/')) ?? message.MsgType;
    a.href = safeSrc;
    a.click();
  };

  return (
    <PageLayout
      headerConfig={{
        navigateBack: true,
        className: cn('transition-colors', { 'bg-neutral-950 text-neutral-0': shouldDarkMode }),
        headerLabel: message && (
          <div className='flex flex-col text-sm size-full flex-center'>
            <span className='font-bold'>{message.UserNickName}</span>
            <DateDisplay
              ruleName='collection-preview'
              timestamp={message.CreateTime}
            />
          </div>
        ),
        actions: (
          <NativeShareButton
            className={cn(' text-white', {
              'text-base-content':
                message?.MsgType &&
                [ConversationMessageContentType.File, ConversationMessageContentType.Text].includes(message.MsgType),
            })}
            content={{ type: 'message', data: message }}
          />
        ),
      }}
      bannerColor='dark'
      className={cn('flex flex-col gap-1 transition-colors', { 'bg-neutral-950 text-neutral-0': shouldDarkMode })}>
      <div
        className={cn('flex flex-1 flex-col mt-4', {
          'mx-5': [ConversationMessageContentType.Text, ConversationMessageContentType.File].includes(message.MsgType),
        })}>
        <MediaContent
          autoPlay
          isInView
          type={message.MsgType}
          src={message.Message}
        />

        <div className='py-6 flex items-center justify-around'>
          {[
            ConversationMessageContentType.Image,
            ConversationMessageContentType.Video,
            ConversationMessageContentType.File,
          ].includes(message.MsgType) && (
            <button
              type='button'
              className='btn btn-ghost flex-nowrap flex-col gap-2'
              onClick={handleDownload}>
              <ImportCurve
                variant='Outline'
                size={24}
              />
              <span>{t('conversation.setting.group.qr-code.save-image')}</span>
            </button>
          )}
          {message.MsgType === ConversationMessageContentType.Text && (
            <button
              type='button'
              className='btn btn-ghost flex-nowrap flex-col gap-2'
              onClick={() => copyTextToClipboard(message.Message)}>
              <Copy
                variant='Outline'
                size={24}
              />
              <span>{t('common.copy')}</span>
            </button>
          )}
        </div>
      </div>
    </PageLayout>
  );
}
